import { useEffect, useMemo, useState } from 'react';
import { CalendarClock, CheckCircle2, ExternalLink, RotateCcw } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { useShellTitle } from '../components/layout/ShellTitleContext';
import Card from '../components/ui/Card';
import Spinner from '../components/ui/Spinner';
import { StatusBadge } from '../features/learnerPlan';
import { getPlan, type LearnerPlanDto, type AssessmentItemDto } from '../features/learnerPlan/learnerPlanApi';

// /learner-plan — every DC Connect instrument this course tracks, its
// class-time plan, and (when known) the mirrored D2L status. D2L stays the
// system of record; this page never lets a student submit or attempt
// anything — every link out goes to D2L itself.
//
// Laid out around what the instructor asked for after the first real class:
// use the width the Teach shell already uses, lead with what needs action
// (retry → due → done, in that order), and make the status control read as
// its own shape, not just a colour (features/learnerPlan StatusBadge).

const ITEM_TYPE_LABEL: Record<AssessmentItemDto['item_type'], string> = {
  quiz: 'Quiz',
  learning_activity: 'Learning Activity',
  milestone: 'Milestone',
  culminating_task: 'Culminating Task',
  assignment: 'Assignment',
  test: 'Test',
};

type Bucket = 'retry' | 'due' | 'done';

function statusBucket(item: AssessmentItemDto): Bucket {
  const s = item.my_status?.status;
  if (s === 'needs_retry') return 'retry';
  if (s === 'passed' || s === 'graded') return 'done';
  return 'due';
}

function sessionText(s: AssessmentItemDto['due_at']): string {
  if (!s) return '—';
  return s.label || `Meeting ${s.seq}`;
}

// The content models "Milestone A" / "Milestone B" as GROUPS — each bundles
// several separately-graded items (including the individually-numbered
// Milestones 36, 221 and 53) behind one open/due window. That's the source
// of the instructor's "what's Milestone A and B, we have 3 numbered
// milestones" question, still open (see report). Renaming isn't ours to
// decide, so this card makes the distinction legible without changing a
// label: an explicit "Group" tag plus the member items it actually contains.
function GroupCard({ group, items }: { group: LearnerPlanDto['groups'][number]; items: AssessmentItemDto[] }) {
  const members = items.filter((it) => it.group_keys.includes(group.key));
  return (
    <Card className="p-4">
      <div className="flex items-center gap-2">
        <span className="rounded-md bg-ws-surface-2 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-ws-ink-3">
          Group
        </span>
        <h3 className="text-sm font-semibold text-ws-ink">{group.title}</h3>
      </div>
      <dl className="mt-2.5 grid grid-cols-2 gap-x-3 gap-y-1 text-[12.5px] text-ws-ink-2">
        <dt className="text-ws-ink-3">Opens</dt>
        <dd>{sessionText(group.opens_at)}</dd>
        <dt className="text-ws-ink-3">Due</dt>
        <dd>{sessionText(group.due_at)}</dd>
      </dl>
      {members.length > 0 && (
        <p className="mt-2.5 border-t border-ws-border pt-2 text-[11.5px] leading-relaxed text-ws-ink-3">
          <span className="font-medium text-ws-ink-2">{members.length} items in this group — </span>
          {members.map((m) => m.title).join(' · ')}
        </p>
      )}
    </Card>
  );
}

function SummaryStrip({ buckets }: { buckets: Record<Bucket, AssessmentItemDto[]> }) {
  const tiles: Array<{ key: Bucket; label: string; icon: LucideIcon; tone: string; bg: string }> = [
    { key: 'retry', label: 'Needs a retry', icon: RotateCcw, tone: 'text-ws-danger', bg: 'border-ws-danger/25 bg-ws-danger/10' },
    { key: 'due', label: 'Due', icon: CalendarClock, tone: 'text-ws-accent-ink', bg: 'border-ws-accent/25 bg-ws-accent-quiet' },
    { key: 'done', label: 'Done', icon: CheckCircle2, tone: 'text-ws-success', bg: 'border-ws-success/25 bg-ws-success-quiet' },
  ];
  return (
    // Three tiles of icon + number + a two-to-three-word label. At 390px a
    // third of the page is ~98px, less p-3.5 and a 20px icon, which left about
    // 38px for "Needs a retry" — four wrapped lines each, on the page a
    // student checks most often. One column on a phone, three from sm up.
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
      {tiles.map((t) => (
        <div key={t.key} className={`flex items-center gap-3 rounded-md border p-3.5 ${t.bg}`}>
          <t.icon size={20} className={`shrink-0 ${t.tone}`} aria-hidden="true" />
          <div>
            <div className={`text-xl font-semibold leading-none ${t.tone}`}>{buckets[t.key].length}</div>
            <div className="mt-1 text-[11.5px] font-medium text-ws-ink-2">{t.label}</div>
          </div>
        </div>
      ))}
    </div>
  );
}

function ItemRow({ item }: { item: AssessmentItemDto }) {
  const due = item.due_at ? sessionText(item.due_at) : (item.group_keys.length ? `follows ${item.group_keys.join('/')}` : '—');
  return (
    <tr className="border-b border-ws-border last:border-0 hover:bg-ws-surface-2/60">
      <td className="px-4 py-2.5 font-medium text-ws-ink">{item.title}</td>
      <td className="px-3 py-2.5 text-ws-ink-2">
        {ITEM_TYPE_LABEL[item.item_type]}
        {item.points != null && <span className="text-ws-ink-3"> · {item.points} pts</span>}
      </td>
      <td className="px-3 py-2.5 text-ws-ink-2">{item.group_keys.length ? item.group_keys.join(', ') : '—'}</td>
      <td className="px-3 py-2.5 text-ws-ink-2">{item.intro_session ? sessionText(item.intro_session) : '—'}</td>
      <td className="px-3 py-2.5 text-ws-ink-2">{due}</td>
      <td className="px-3 py-2.5">
        {item.d2l_url ? (
          <a href={item.d2l_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-ws-accent hover:underline">
            Open in D2L <ExternalLink size={12} />
          </a>
        ) : (
          <span className="text-ws-ink-4">D2L link pending</span>
        )}
      </td>
      <td className="px-4 py-2.5"><StatusBadge status={item.my_status} /></td>
    </tr>
  );
}

function ItemSection({
  icon: Icon, tone, title, hint, items, emptyText,
}: {
  icon: LucideIcon;
  tone: 'retry' | 'due' | 'done';
  title: string;
  hint: string;
  items: AssessmentItemDto[];
  emptyText: string;
}) {
  const iconTone = tone === 'retry' ? 'text-ws-danger' : tone === 'done' ? 'text-ws-success' : 'text-ws-accent';
  return (
    <Card className={`overflow-hidden ${tone === 'retry' && items.length > 0 ? 'border-ws-danger/30' : ''}`}>
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-ws-border px-4 py-3">
        <div className="flex items-center gap-2">
          <Icon size={16} className={iconTone} aria-hidden="true" />
          <h2 className="text-sm font-semibold text-ws-ink">{title}</h2>
          <span className="rounded-md bg-ws-surface-2 px-1.5 py-0.5 text-[11px] font-semibold text-ws-ink-3">{items.length}</span>
        </div>
        <p className="text-[11.5px] text-ws-ink-3">{hint}</p>
      </div>

      {items.length === 0 ? (
        <p className="px-4 py-4 text-[13px] text-ws-ink-3">{emptyText}</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[860px] text-left text-[13px]">
            <thead>
              <tr className="border-b border-ws-border text-[11px] uppercase tracking-wide text-ws-ink-3">
                <th className="px-4 pb-2 pt-3 font-semibold">Item</th>
                <th className="px-3 pb-2 pt-3 font-semibold">Type</th>
                <th className="px-3 pb-2 pt-3 font-semibold">Group</th>
                <th className="px-3 pb-2 pt-3 font-semibold">Introduce</th>
                <th className="px-3 pb-2 pt-3 font-semibold">Due</th>
                <th className="px-3 pb-2 pt-3 font-semibold">D2L</th>
                <th className="px-4 pb-2 pt-3 font-semibold">Status</th>
              </tr>
            </thead>
            <tbody>
              {items.map((it) => <ItemRow key={it.key} item={it} />)}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}

export default function LearnerPlanPage() {
  useShellTitle('Learner plan');
  const [plan, setPlan] = useState<LearnerPlanDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    getPlan()
      .then((data) => { if (!cancelled) setPlan(data); })
      .catch(() => { if (!cancelled) setError(true); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const buckets = useMemo<Record<Bucket, AssessmentItemDto[]>>(() => {
    const result: Record<Bucket, AssessmentItemDto[]> = { retry: [], due: [], done: [] };
    for (const item of plan?.items ?? []) result[statusBucket(item)].push(item);
    return result;
  }, [plan]);

  return (
    <div className="ws-page space-y-5">
      <header>
        <h1 className="text-xl font-semibold text-ws-ink">Learner plan</h1>
        <p className="mt-1 text-sm text-ws-ink-3">
          Every quiz, learning activity, milestone, assignment and test in this course lives in
          DC Connect (D2L) — this page just plans class time around each one and shows what's
          known here about your status.
        </p>
      </header>

      {loading && (
        <Card className="p-6">
          <Spinner label="Loading your learner plan…" />
        </Card>
      )}

      {!loading && error && (
        <Card className="p-6">
          <p className="text-sm text-ws-ink-3">Couldn't load the learner plan right now. Try again shortly.</p>
        </Card>
      )}

      {!loading && !error && plan && (
        <>
          {plan.items.length > 0 && <SummaryStrip buckets={buckets} />}

          {plan.groups.length > 0 && (
            <section>
              <h2 className="mb-2 text-xs font-semibold uppercase tracking-wide text-ws-ink-3">
                Assessment groups <span className="font-normal normal-case text-ws-ink-4">— each bundles several items behind one open/due window</span>
              </h2>
              <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
                {plan.groups.map((g) => <GroupCard key={g.key} group={g} items={plan.items} />)}
              </div>
            </section>
          )}

          {plan.items.length > 0 && (
            <div className="space-y-4">
              <ItemSection
                icon={RotateCcw}
                tone="retry"
                title="Needs a retry"
                hint="Marked needs-retry — resubmit or retake in D2L."
                items={buckets.retry}
                emptyText="Nothing needs a retry right now."
              />
              <ItemSection
                icon={CalendarClock}
                tone="due"
                title="Due"
                hint="Not yet passed or graded."
                items={buckets.due}
                emptyText="Nothing outstanding right now."
              />
              <ItemSection
                icon={CheckCircle2}
                tone="done"
                title="Done"
                hint="Passed or graded."
                items={buckets.done}
                emptyText="Nothing marked done yet."
              />
            </div>
          )}

          {plan.items.length === 0 && (
            <Card className="p-6">
              <p className="py-4 text-center text-sm text-ws-ink-3">Nothing has been planned yet.</p>
            </Card>
          )}
        </>
      )}
    </div>
  );
}
